import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["questionKey"];
import { View } from 'foundations-components/transitional/components/View';
import { PPUIPageHeader } from 'pricing-pages-ui-components/semantic/PageHeader';
import { PageSection } from 'pricing-pages-ui-components/semantic/PageSection';
import Raven from 'raven-js';
import { useEffect, useState } from 'react';
import { buildFilteredHighlightedProductsV2 } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import BannerSection from '../components/banners/BannerSection';
import ProductPageSubtitle from '../components/common/ProductPageSubtitle';
import ProductPageTitle from '../components/common/ProductPageTitle';
import { CreditsCallout } from '../components/common/credits-callout/CreditsCallout';
import FallbackErrorBanner from '../components/common/loading/FallbackErrorBanner';
import Faq from '../components/faqs/Faq';
import Question from '../components/faqs/Question';
import ProductFeatureTable from '../components/feature-table/ProductFeatureTable';
import { FreeCard } from '../components/merch-card/cards/FreeCard';
import { HubCard } from '../components/merch-card/cards/HubCard';
import MobileMerchCardSelectorButtons from '../components/mobile/MobileMerchCardSelectorButtons';
import MobileCardWrapper from '../components/mobile/containers/MobileCardWrapper';
import MobileWrapper from '../components/mobile/containers/MobileWrapper';
import { PRODUCT_PAGE, PRODUCT_PAGE_CONTENT } from '../constants/ErrorBoundaryNames';
import { DEFAULT } from '../constants/ErrorCauses';
import { FaqsConfig } from '../constants/faqs/FaqsConfig';
import { getMobileMerchCardsToShow } from '../constants/merchCards/MerchCardsToShow';
import { useAnchorLinkScroll } from '../hooks/htmlElementHooks';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { getNavItemForProductLine } from '../utils/routingUtils';
import { useQueryParamTerm, useQueryParamsEffect } from '../utils/urls/appQueryParamUtilsLegacy';
import { ErrorBoundary } from '../v2/components/error-boundary/ErrorBoundary';
import { ERROR_SEVERITY } from '../v2/components/error-boundary/ErrorSeverity';
import withErrorBoundary from '../v2/components/error-boundary/withErrorBoundary';
import PhoneSupportFooter from '../v2/components/phone-support/PhoneSupportFooter';
import { FREE_CARD, TRACKING_SOURCES } from '../v2/constants/tracking/TrackingSources';
import { IntegrationsSection } from '../v2/containers/IntegrationsSection';
import { trackPageView } from '../v2/utils/tracking/trackingEventUtils';
import ErrorPage from './ErrorPage';
import MissingInfoFallbackPage from './MissingInfoFallbackPage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MobileProductPage = ({
  productLine
}) => {
  useAnchorLinkScroll();
  useDocumentTitle(productLine.toLowerCase());
  const navItem = getNavItemForProductLine(productLine);
  const faqsConfig = FaqsConfig[navItem];
  useEffect(() => {
    if (!navItem) {
      Raven.captureMessage('ProductPage screen is undefined', {
        extra: {
          productLine
        }
      });
      return;
    }
    trackPageView({
      screen: navItem
    });
  }, [productLine, navItem]);
  const productLevels = getMobileMerchCardsToShow();
  const {
    queryParamTerm
  } = useQueryParamTerm();
  useQueryParamsEffect({
    term: queryParamTerm
  });
  const [selectedProductLevel, setSelectedProductLevel] = useState(productLevels[0]);
  const filteredHighlightedProducts = buildFilteredHighlightedProductsV2({
    productLevels: [selectedProductLevel],
    productLine
  });
  const apiName = (filteredHighlightedProducts === null || filteredHighlightedProducts === void 0 ? void 0 : filteredHighlightedProducts.find(name => name.includes('starter'))) || (filteredHighlightedProducts === null || filteredHighlightedProducts === void 0 ? void 0 : filteredHighlightedProducts[0]);
  return /*#__PURE__*/_jsx(ErrorBoundary, {
    fallback: /*#__PURE__*/_jsx(MissingInfoFallbackPage, {
      navItem: navItem
    }),
    errorBoundaryName: PRODUCT_PAGE_CONTENT,
    severity: ERROR_SEVERITY.HIGH,
    children: /*#__PURE__*/_jsxs(MobileWrapper, {
      navMarkerName: `${productLine}_PAGE`,
      navItem: navItem,
      children: [/*#__PURE__*/_jsx(BannerSection, {
        productLine: productLine,
        screen: navItem
      }), /*#__PURE__*/_jsx(FallbackErrorBanner, {}), /*#__PURE__*/_jsxs(PPUIPageHeader, {
        children: [/*#__PURE__*/_jsx(ProductPageTitle, {
          productLine: productLine
        }), /*#__PURE__*/_jsx(ProductPageSubtitle, {
          productLine: productLine
        })]
      }), /*#__PURE__*/_jsxs(PageSection, {
        children: [/*#__PURE__*/_jsx(MobileMerchCardSelectorButtons, {
          options: productLevels,
          onClick: setSelectedProductLevel,
          selectedOption: selectedProductLevel
        }), /*#__PURE__*/_jsx(MobileCardWrapper, {
          children: selectedProductLevel === 'FREE' ? /*#__PURE__*/_jsx(FreeCard, {
            productLine: productLine,
            columnStart: 1,
            trackingSource: FREE_CARD
          }) : /*#__PURE__*/_jsx(HubCard, {
            productLevel: selectedProductLevel,
            productLine: productLine,
            columnStart: 1
          })
        }), /*#__PURE__*/_jsx(View, {
          marginBlock: "800",
          children: /*#__PURE__*/_jsx(CreditsCallout, {
            navItem: navItem
          })
        }), /*#__PURE__*/_jsx(ProductFeatureTable, {
          productLine: productLine,
          highlightedProducts: filteredHighlightedProducts,
          screen: navItem
        }, productLine)]
      }), /*#__PURE__*/_jsx(IntegrationsSection, {}), /*#__PURE__*/_jsx(Faq, {
        children: faqsConfig.map(_ref => {
          let {
              questionKey
            } = _ref,
            passthroughOptions = _objectWithoutPropertiesLoose(_ref, _excluded);
          return /*#__PURE__*/_jsx(Question, Object.assign({
            questionKey: questionKey
          }, passthroughOptions), questionKey);
        })
      }), /*#__PURE__*/_jsx(PhoneSupportFooter, {
        phoneSourceId: TRACKING_SOURCES[`${productLine}_FOOTER`] || TRACKING_SOURCES.MOBILE_PRODUCT_PAGE,
        screen: navItem,
        apiName: apiName
      })]
    })
  });
};
export default withErrorBoundary(MobileProductPage, {
  FallbackComponent: () => /*#__PURE__*/_jsx(ErrorPage, {
    errorCause: DEFAULT
  }),
  errorBoundaryName: PRODUCT_PAGE,
  severity: ERROR_SEVERITY.HIGH
});